import { useEffect, useMemo, useState } from "react";
import { Interweave } from "interweave";
import { ScrollTop } from "primereact/scrolltop";
import { useHistory, useParams } from "react-router-dom";
import { toastr } from "react-redux-toastr";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap";
import {
  AlertTriangle,
  BarChart2,
  Bell,
  Cpu,
  Edit3,
  FileText,
  Flag,
  Lock,
  MessageCircle,
  RefreshCw,
  Search,
  Tool,
  Zap,
} from "react-feather";

import api from "../../services/api";
import "./styles.css";

type InformeCategoria =
  | "Campanha"
  | "Comunicado"
  | "Atualização"
  | "Novidade"
  | "Operacional"
  | "Segurança"
  | "Relatórios"
  | "Integração"
  | "Tecnologia";

type GrupoInformes = "Hoje" | "Ontem" | "Anteriores";

type InformeIcone = keyof typeof iconMap;

interface InformeItem {
  hash: string;
  destaque?: boolean;
  categoria: InformeCategoria;
  icon: InformeIcone;
  hero: string;
  titulo: string;
  data: string;
  resumo: string;
  texto: string;
}

interface Params {
  hash?: string;
}

const iconMap = {
  campanha: Flag,
  comunicado: AlertTriangle,
  atualizacao: Edit3,
  novidade: Zap,
  dashboard: BarChart2,
  seguranca: Lock,
  operacional: Tool,
  historico: FileText,
  integracao: MessageCircle,
  tecnologia: Cpu,
};

const informes: InformeItem[] = [
  {
    hash: "brasil-japao-appsupply",
    destaque: true,
    categoria: "Campanha",
    icon: "campanha",
    hero: "linear-gradient(135deg,#009b3a 0%,#fedf00 52%,#002776 100%)",
    titulo: "Brasil x Japão: faça parte dessa seleção",
    data: "Hoje • 09:00",
    resumo:
      "Acompanhe os alertas pelo AppSupply e entre em campo com sua operação.",
    texto: `
      <p>Hoje é dia de Brasil em campo e também é dia de reforçar a atenção com a sua operação.</p>
      <p>Assim como uma equipe vencedora precisa estar atenta a cada jogada, sua equipe também precisa acompanhar cada alerta crítico no momento certo.</p>
      <div class="informes-reader-quote">Faça parte dessa seleção: acompanhe tudo pelo AppSupply e marque um golaço com sua equipe.</div>
      <h3>Durante o jogo</h3>
      <ul>
        <li>Mantenha as notificações do AppSupply ativadas.</li>
        <li>Acompanhe eventos críticos em tempo real.</li>
        <li>Use a timeline para validar rapidamente os alertas.</li>
      </ul>
      <p>O AppSupply segue sendo o canal central para manter sua operação em campo.</p>
    `,
  },
  {
    hash: "whatsapp-instabilidade",
    categoria: "Comunicado",
    icon: "comunicado",
    hero: "linear-gradient(135deg,#fff7e6,#ffe1a6)",
    titulo: "Instabilidade temporária no WhatsApp",
    data: "Hoje • 15:42",
    resumo:
      "Durante a instabilidade, acompanhe os alertas diretamente pelo AppSupply.",
    texto: `
      <p>Identificamos instabilidade temporária no canal de notificações por WhatsApp.</p>
      <p>Durante este período, acompanhe os alertas críticos diretamente pelo AppSupply e WebApp.</p>
      <h3>Importante</h3>
      <ul>
        <li>O monitoramento continua ativo.</li>
        <li>Os eventos continuam sendo processados normalmente.</li>
        <li>As notificações pelo AppSupply seguem operando.</li>
      </ul>
      <p>Recomendamos manter o AppSupply aberto ou com notificações habilitadas.</p>
    `,
  },
  {
    hash: "nova-justificativa",
    categoria: "Atualização",
    icon: "atualizacao",
    hero: "linear-gradient(135deg,#eaf3ff,#d7eaff)",
    titulo: "Nova área de justificativas disponível",
    data: "Hoje • 14:15",
    resumo:
      "Agora os usuários podem justificar eventos diretamente pelo aplicativo.",
    texto: `
      <p>A partir de agora, os usuários poderão justificar eventos diretamente na experiência do AppSupply.</p>
      <h3>O que muda na prática?</h3>
      <ul>
        <li>Mais agilidade no registro das ocorrências.</li>
        <li>Melhor rastreabilidade operacional.</li>
        <li>Histórico mais claro para auditorias e acompanhamento interno.</li>
      </ul>
      <p>O recurso estará disponível nos eventos compatíveis com justificativa operacional.</p>
    `,
  },
  {
    hash: "appsupply-4",
    categoria: "Novidade",
    icon: "novidade",
    hero: "linear-gradient(135deg,#e7fff5,#c7f7e2)",
    titulo: "Nova versão AppSupply 4.0",
    data: "Hoje • 11:30",
    resumo:
      "Uma experiência mais rápida, limpa e preparada para novos módulos.",
    texto: `
      <p>Estamos preparando uma nova versão do AppSupply com foco em desempenho, clareza visual e melhor experiência mobile.</p>
      <h3>Principais melhorias</h3>
      <ul>
        <li>Navegação mais simples.</li>
        <li>Cards de informação mais modernos.</li>
        <li>Melhor leitura em telas pequenas.</li>
        <li>Base preparada para novos módulos operacionais.</li>
      </ul>
      <p>A liberação será gradual para os usuários habilitados.</p>
    `,
  },
  {
    hash: "dashboard-reformulado",
    categoria: "Atualização",
    icon: "dashboard",
    hero: "linear-gradient(135deg,#edf3ff,#dbe7ff)",
    titulo: "Dashboard reformulado",
    data: "Ontem • 09:20",
    resumo:
      "Indicadores mais claros para acompanhar a operação em tempo real.",
    texto: `
      <p>O dashboard do AppSupply está sendo reformulado para oferecer uma leitura mais objetiva dos indicadores da operação.</p>
      <h3>O que vem por aí</h3>
      <ul>
        <li>Gráficos mais rápidos.</li>
        <li>Indicadores agrupados por prioridade.</li>
        <li>Melhor organização por unidade e ambiente.</li>
        <li>Experiência aprimorada no celular.</li>
      </ul>
      <p>Essa atualização reforça o AppSupply como centro de comando operacional.</p>
    `,
  },
  {
    hash: "seguranca-reforcada",
    categoria: "Segurança",
    icon: "seguranca",
    hero: "linear-gradient(135deg,#f0f4f8,#dde5ee)",
    titulo: "Sua conta ficou mais protegida",
    data: "26 Junho • 14:10",
    resumo: "Novas camadas de proteção foram adicionadas ao AppSupply.",
    texto: `
      <p>Implementamos novas camadas de segurança para proteger o acesso dos usuários ao AppSupply.</p>
      <h3>Recursos adicionados</h3>
      <ul>
        <li>Validação reforçada de sessão.</li>
        <li>Melhoria no controle de dispositivos.</li>
        <li>Proteções adicionais contra acessos indevidos.</li>
      </ul>
      <p>Nenhuma ação é necessária por parte do usuário.</p>
    `,
  },
  {
    hash: "manutencao-programada",
    categoria: "Operacional",
    icon: "operacional",
    hero: "linear-gradient(135deg,#fff4e6,#ffe0bd)",
    titulo: "Manutenção programada",
    data: "25 Junho • 21:30",
    resumo: "Melhorias preventivas serão aplicadas na infraestrutura.",
    texto: `
      <p>Realizaremos uma manutenção preventiva para melhoria de desempenho e estabilidade da plataforma.</p>
      <h3>Durante esse período</h3>
      <ul>
        <li>O monitoramento permanecerá ativo.</li>
        <li>Os alarmes continuarão sendo processados.</li>
        <li>Alguns recursos administrativos poderão oscilar por poucos minutos.</li>
      </ul>
      <p>Assim que a manutenção for concluída, a operação será normalizada automaticamente.</p>
    `,
  },
  {
    hash: "historico-informes",
    categoria: "Novidade",
    icon: "historico",
    hero: "linear-gradient(135deg,#edf7ff,#d7efff)",
    titulo: "Histórico de informes disponível",
    data: "24 Junho • 18:20",
    resumo:
      "Todos os comunicados importantes agora ficam organizados em uma área própria.",
    texto: `
      <p>Agora todos os comunicados enviados pela Supply ficam armazenados em uma área própria no AppSupply.</p>
      <h3>Você poderá consultar</h3>
      <ul>
        <li>Atualizações do sistema.</li>
        <li>Avisos operacionais.</li>
        <li>Manutenções programadas.</li>
        <li>Campanhas e comunicados importantes.</li>
      </ul>
      <p>Links como <strong>/info/historico-informes</strong> podem abrir diretamente o informe em tela cheia.</p>
    `,
  },
  {
    hash: "relatorio-mensal",
    categoria: "Relatórios",
    icon: "historico",
    hero: "linear-gradient(135deg,#f3ecff,#e2d4ff)",
    titulo: "Relatórios mensais em evolução",
    data: "23 Junho • 11:15",
    resumo:
      "A área de relatórios receberá melhorias de leitura e exportação.",
    texto: `
      <p>Estamos evoluindo os relatórios mensais para facilitar a análise de conformidade e recorrência dos eventos.</p>
      <h3>Em breve</h3>
      <ul>
        <li>Resumo executivo por unidade.</li>
        <li>Indicadores de recorrência.</li>
        <li>Exportação otimizada em PDF.</li>
        <li>Melhor visualização mobile.</li>
      </ul>
    `,
  },
  {
    hash: "meta-oficial",
    categoria: "Integração",
    icon: "integracao",
    hero: "linear-gradient(135deg,#e9fff4,#ccf5df)",
    titulo: "Integração com WhatsApp Meta Oficial",
    data: "22 Junho • 16:40",
    resumo:
      "A Supply iniciou a evolução para uma comunicação oficial via Meta API.",
    texto: `
      <p>A Supply está avançando na estrutura de integração com a WhatsApp Meta API Oficial.</p>
      <p>Esse movimento busca aumentar a confiabilidade dos canais de comunicação e reduzir dependências de plataformas não oficiais.</p>
      <h3>Benefícios esperados</h3>
      <ul>
        <li>Mais estabilidade no envio de mensagens.</li>
        <li>Maior aderência às políticas oficiais da Meta.</li>
        <li>Melhor rastreabilidade dos disparos críticos.</li>
      </ul>
    `,
  },
  {
    hash: "mqtt-broker",
    categoria: "Tecnologia",
    icon: "tecnologia",
    hero: "linear-gradient(135deg,#fffbe6,#fff0a8)",
    titulo: "Nova arquitetura MQTT em estudo",
    data: "21 Junho • 10:05",
    resumo:
      "Broker MQTT Supply poderá centralizar dados enviados pelos equipamentos.",
    texto: `
      <p>Estamos estudando uma arquitetura MQTT própria para centralizar a comunicação dos equipamentos em campo.</p>
      <h3>Objetivos</h3>
      <ul>
        <li>Receber dados dos ESP32 com mais eficiência.</li>
        <li>Reduzir latência entre campo e painel.</li>
        <li>Melhorar a escalabilidade dos projetos SMCCA, SMTU e SIMEA.</li>
      </ul>
      <p>Essa iniciativa faz parte da evolução da infraestrutura técnica da Supply.</p>
    `,
  },
];

const categorias = [
  "Todos",
  "Comunicado",
  "Atualização",
  "Novidade",
  "Operacional",
  "Segurança",
  "Campanha",
  "Relatórios",
  "Integração",
  "Tecnologia",
] as const;

type CategoriaFiltro = (typeof categorias)[number];

function getGroupLabel(data: string): GrupoInformes {
  if (data.includes("Hoje")) {
    return "Hoje";
  }

  if (data.includes("Ontem")) {
    return "Ontem";
  }

  return "Anteriores";
}

function renderIcon(icon: InformeIcone, featured = false) {
  const Icon = iconMap[icon];

  return <Icon size={featured ? 52 : 28} />;
}

export default function Informes() {
  const history = useHistory();
  const { hash } = useParams<Params>();
  const [categoriaAtual, setCategoriaAtual] = useState<CategoriaFiltro>("Todos");
  const [buscaAtual, setBuscaAtual] = useState("");

  const listaFiltrada = useMemo(() => {
    return informes.filter((item) => {
      const categoriaOk =
        categoriaAtual === "Todos" || item.categoria === categoriaAtual;
      const textoBusca = [
        item.titulo,
        item.resumo,
        item.categoria,
        item.data,
        item.texto,
      ]
        .join(" ")
        .toLowerCase();

      return categoriaOk && textoBusca.includes(buscaAtual.toLowerCase());
    });
  }, [buscaAtual, categoriaAtual]);

  const informeSelecionado = useMemo(() => {
    if (!hash) {
      return null;
    }

    return informes.find((item) => item.hash === hash) || null;
  }, [hash]);

  const destaque =
    (categoriaAtual === "Todos"
      ? listaFiltrada.find((item) => item.destaque)
      : null) || listaFiltrada[0];

  const grupos = useMemo(() => {
    return listaFiltrada
      .filter((item) => item.hash !== destaque?.hash)
      .reduce<Record<GrupoInformes, InformeItem[]>>(
        (acc, item) => {
          const grupo = getGroupLabel(item.data);
          acc[grupo].push(item);
          return acc;
        },
        {
          Hoje: [],
          Ontem: [],
          Anteriores: [],
        }
      );
  }, [destaque?.hash, listaFiltrada]);

  useEffect(() => {
    api.post("/logs", {
      url: hash ? `/info/${hash}` : "/info",
    });
  }, [hash]);

  useEffect(() => {
    if (hash && !informeSelecionado) {
      history.replace("/info");
    }
  }, [hash, history, informeSelecionado]);

  const abrirInforme = (nextHash: string) => {
    history.push(`/info/${nextHash}`);
  };

  const fecharInforme = () => {
    history.push("/info");
  };

  const copiarLink = async () => {
    if (!informeSelecionado) {
      return;
    }

    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(
          `${window.location.origin}/info/${informeSelecionado.hash}`
        );
      }

      toastr.success("", "Link do informe copiado.");
    } catch (error) {
      toastr.error("", "Não foi possível copiar o link do informe.");
    }
  };

  return (
    <>
      <Container fluid className="informes-page">
        <Row className="mb-3 align-items-start">
          <Col md="8" className="mb-3 mb-md-0">
            <div className="contentHeader informes-header-block">
              <div>
                <h4 className="mb-1">INFORMES</h4>
                <p className="informes-page-subtitle mb-0">
                  Comunicados, novidades e avisos importantes da Supply
                  Monitoring.
                </p>
              </div>
            </div>
          </Col>

          <Col md="4">
            <div className="informes-actions">
              <div className="informes-notification-pill">
                <Bell size={16} />
                <span>6 novos</span>
              </div>

              <Button color="primary" onClick={() => history.go(0)}>
                <RefreshCw size={16} className="mr-2" />
                Atualizar
              </Button>
            </div>
          </Col>
        </Row>

        <Row className="mb-4">
          <Col lg="6" className="mb-3 mb-lg-0">
            <div className="informes-search-wrap">
              <Search size={18} className="informes-search-icon" />
              <Input
                className="informes-search-input"
                placeholder="Buscar informe"
                value={buscaAtual}
                onChange={(event) => setBuscaAtual(event.target.value)}
              />
            </div>
          </Col>

          <Col lg="6">
            <div className="informes-filter-list">
              {categorias.map((categoria) => (
                <Button
                  key={categoria}
                  type="button"
                  color={categoriaAtual === categoria ? "primary" : "light"}
                  className="informes-filter-button"
                  onClick={() => setCategoriaAtual(categoria)}
                >
                  {categoria}
                </Button>
              ))}
            </div>
          </Col>
        </Row>

        {listaFiltrada.length === 0 ? (
          <Card className="shadow-sm border-0 informes-empty-card">
            <CardBody className="text-center text-muted font-weight-bold py-5">
              Nenhum informe encontrado.
            </CardBody>
          </Card>
        ) : (
          <>
            {destaque && (
              <Row className="mb-4">
                <Col>
                  <div className="informes-section-label">Destaque</div>
                  <Card
                    className="informes-featured-card border-0"
                    onClick={() => abrirInforme(destaque.hash)}
                    role="button"
                  >
                    <div
                      className="informes-featured-hero"
                      style={{ background: destaque.hero }}
                    >
                      {renderIcon(destaque.icon, true)}
                    </div>

                    <CardBody>
                      <Badge color="light" pill className="informes-badge mb-3">
                        {destaque.categoria}
                      </Badge>
                      <h3 className="informes-featured-title">{destaque.titulo}</h3>
                      <p className="informes-featured-summary mb-3">
                        {destaque.resumo}
                      </p>
                      <small className="text-white-50 font-weight-bold">
                        {destaque.data}
                      </small>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            )}

            {(["Hoje", "Ontem", "Anteriores"] as GrupoInformes[]).map((grupo) => {
              if (!grupos[grupo].length) {
                return null;
              }

              return (
                <div key={grupo} className="mb-4">
                  <div className="informes-section-label">{grupo}</div>

                  <Row>
                    {grupos[grupo].map((item) => (
                      <Col xl="4" md="6" className="mb-3" key={item.hash}>
                        <Card
                          className="informes-news-card h-100 border-0"
                          onClick={() => abrirInforme(item.hash)}
                          role="button"
                        >
                          <CardBody>
                            <div className="informes-news-meta mb-3">
                              <div
                                className="informes-news-icon"
                                style={{ background: item.hero }}
                              >
                                {renderIcon(item.icon)}
                              </div>

                              <Badge color="primary" pill>
                                {item.categoria}
                              </Badge>
                            </div>
                            <h5 className="informes-news-title">{item.titulo}</h5>
                            <p className="informes-news-summary">{item.resumo}</p>
                            <small className="text-muted font-weight-bold">
                              {item.data}
                            </small>
                          </CardBody>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </div>
              );
            })}
          </>
        )}
      </Container>

      <Modal
        isOpen={Boolean(informeSelecionado)}
        toggle={fecharInforme}
        size="lg"
        centered
        className="informes-modal"
      >
        {informeSelecionado && (
          <>
            <ModalHeader toggle={fecharInforme} className="border-0 pb-0" />

            <ModalBody className="pt-0">
              <div
                className="informes-modal-hero"
                style={{ background: informeSelecionado.hero }}
              >
                {renderIcon(informeSelecionado.icon, true)}
              </div>

              <div className="informes-modal-meta">
                <Badge color="primary" pill>
                  {informeSelecionado.categoria}
                </Badge>
              </div>

              <div className="mt-4 d-flex justify-content-between align-items-start flex-wrap">
                <div className="pr-md-3">
                  <h3 className="informes-modal-title">{informeSelecionado.titulo}</h3>
                  <p className="text-muted font-weight-bold mb-3">
                    {informeSelecionado.data}
                  </p>
                </div>

                <Button color="secondary" outline onClick={copiarLink}>
                  Copiar link
                </Button>
              </div>

              <div className="informes-modal-content">
                <Interweave content={informeSelecionado.texto} />
              </div>
            </ModalBody>
          </>
        )}
      </Modal>

      <ScrollTop style={{ bottom: "80px" }} />
    </>
  );
}
